import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
  Chip,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Tooltip,
  Grid,
  Card,
  CardContent,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  DirectionsRun as DirectionsIcon,
  Assignment as RouteIcon,
  PersonRemove as RemoveRiderIcon,
  PersonAdd as AssignRiderIcon
} from '@mui/icons-material';
import { useApp } from '../../context/AppContext';
import { appConfig } from '../../config/app';
import RouteDetailsModal from '../../components/common/RouteDetailsModal';

const Routes = () => {
  const { actions } = useApp();
  
  // State management
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [riderFilter, setRiderFilter] = useState('all');
  const [riders, setRiders] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0
  });
  
  // Modal state
  const [routeDetailsModalOpen, setRouteDetailsModalOpen] = useState(false);
  const [selectedRouteId, setSelectedRouteId] = useState(null);
  
  // De-assignment and Re-assignment state
  const [deassignDialog, setDeassignDialog] = useState({
    open: false,
    route: null,
    loading: false
  });
  
  const [assignDialog, setAssignDialog] = useState({
    open: false,
    route: null,
    selectedRider: null,
    loading: false,
    searchTerm: '',
    searchResults: [],
    searchLoading: false
  });

  // Route status configuration
  const getStatusColor = (status) => {
    switch (status) {
      case 'created': return 'default';
      case 'assigned': return 'primary';
      case 'active': return 'warning';
      case 'completed': return 'success';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'created': return 'Created';
      case 'assigned': return 'Assigned';
      case 'active': return 'Active';
      case 'completed': return 'Completed';
      case 'cancelled': return 'Cancelled';
      default: return status;
    }
  };

  // Fetch routes from API
  const fetchRoutes = async () => {
    setLoading(true);
    setError('');
    
    try {
      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
        ...(statusFilter !== 'all' && { status: statusFilter }),
        ...(riderFilter !== 'all' && { riderId: riderFilter }),
        ...(searchTerm && { search: searchTerm })
      });

      const response = await fetch(`${appConfig.api.baseURL}/delivery-routes?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem(appConfig.storage.token)}`
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: Failed to fetch routes`);
      }

      const data = await response.json();
      
      if (data.success) {
        setRoutes(data.routes || []);
        setPagination(prev => ({
          ...prev,
          total: data.total || 0
        }));
      } else {
        throw new Error(data.message || 'Failed to fetch routes');
      }
    } catch (error) {
      console.error('Error fetching routes:', error);
      setError(error.message);
      actions.showSnackbar('Error fetching routes', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Fetch riders from API
  const fetchRiders = async () => {
    try {
      const response = await fetch(`${appConfig.api.baseURL}/users/riders/list?limit=100`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem(appConfig.storage.token)}`
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: Failed to fetch riders`);
      }

      const data = await response.json();
      
      if (data.success) {
        setRiders(data.riders || []);
      } else {
        console.warn('Failed to fetch riders:', data.message);
        setRiders([]);
      }
    } catch (error) {
      console.error('Error fetching riders:', error);
      setRiders([]);
    }
  };

  // Format date/time for display
  const formatDateTime = (timestamp) => {
    if (!timestamp) return '-';
    return new Date(timestamp).toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Handle route actions
  const handleViewRoute = (route) => {
    setSelectedRouteId(route.routeId);
    setRouteDetailsModalOpen(true);
  };

  const handleTrackRoute = (route) => {
    // Implement route tracking
    actions.showSnackbar(`Tracking route ${route.routeId}`, 'info');
  };
  
  // Handle de-assignment
  const handleOpenDeassignDialog = (route) => {
    setDeassignDialog({
      open: true,
      route,
      loading: false
    });
  };
  
  const handleCloseDeassignDialog = () => {
    setDeassignDialog({
      open: false,
      route: null,
      loading: false
    });
  };
  
  const handleConfirmDeassign = async () => {
    const route = deassignDialog.route;
    if (!route) return;
    
    setDeassignDialog(prev => ({ ...prev, loading: true }));
    
    try {
      // Step 1: Remove rider from route
      const routeUpdateResponse = await fetch(`${appConfig.api.baseURL}/delivery-routes/${route.routeId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem(appConfig.storage.token)}`
        },
        body: JSON.stringify({
          updateData: {
            deliveryPersonId: null,
            deliveryPersonName: null,
            deliveryPersonMobile: null,
            assignedAt: null,
            status: 'created' // Reset status back to created
          }
        })
      });
      
      if (!routeUpdateResponse.ok) {
        throw new Error('Failed to update route');
      }
      
      // Step 2: Remove rider and routeId from all orders in the route
      const ordersUpdateResponse = await fetch(`${appConfig.api.baseURL}/orders/bulk-update-orders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem(appConfig.storage.token)}`
        },
        body: JSON.stringify({
          orderIds: route.orderIds || [],
          updateData: {
            deliveryPersonMobile: null,
            deliveryPersonName: null,
            routeId: null
          }
        })
      });
      
      if (!ordersUpdateResponse.ok) {
        throw new Error('Failed to update orders');
      }
      
      actions.showSnackbar(`Rider removed from route ${route.routeId} successfully`, 'success');
      handleCloseDeassignDialog();
      fetchRoutes(); // Refresh routes list
    } catch (error) {
      console.error('Error de-assigning rider:', error);
      actions.showSnackbar(error.message || 'Failed to remove rider from route', 'error');
    } finally {
      setDeassignDialog(prev => ({ ...prev, loading: false }));
    }
  };
  
  // Handle initial assignment
  const handleOpenAssignDialog = (route) => {
    setAssignDialog({
      open: true,
      route,
      selectedRider: null,
      loading: false,
      searchTerm: '',
      searchResults: [],
      searchLoading: false
    });
  };
  
  const handleCloseAssignDialog = () => {
    setAssignDialog({
      open: false,
      route: null,
      selectedRider: null,
      loading: false,
      searchTerm: '',
      searchResults: [],
      searchLoading: false
    });
  };

  // Search riders for assign dialog (same API as Riders list page)
  const handleAssignDialogSearchRiders = async () => {
    const search = (assignDialog.searchTerm || '').trim();
    if (!search) {
      actions.showSnackbar('Enter name or mobile number to search', 'warning');
      return;
    }
    setAssignDialog(prev => ({ ...prev, searchLoading: true, searchResults: [] }));
    try {
      const response = await fetch(
        `${appConfig.api.baseURL}/users/riders/list?search=${encodeURIComponent(search)}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem(appConfig.storage.token)}`
          }
        }
      );
      if (!response.ok) throw new Error('Failed to fetch riders');
      const data = await response.json();
      if (data.success) {
        setAssignDialog(prev => ({
          ...prev,
          searchResults: data.riders || [],
          searchLoading: false
        }));
        if (!(data.riders || []).length) {
          actions.showSnackbar('No riders found', 'info');
        }
      } else {
        setAssignDialog(prev => ({ ...prev, searchLoading: false, searchResults: [] }));
        actions.showSnackbar(data.message || 'Search failed', 'error');
      }
    } catch (err) {
      console.error('Error searching riders:', err);
      setAssignDialog(prev => ({ ...prev, searchLoading: false, searchResults: [] }));
      actions.showSnackbar(err.message || 'Failed to search riders', 'error');
    }
  };
  
  const handleConfirmAssign = async () => {
    const { route, selectedRider } = assignDialog;
    if (!route || !selectedRider) return;
    
    setAssignDialog(prev => ({ ...prev, loading: true }));
    
    try {
      // Step 1: Update route with rider
      const routeUpdateResponse = await fetch(`${appConfig.api.baseURL}/delivery-routes/${route.routeId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem(appConfig.storage.token)}`
        },
        body: JSON.stringify({
          updateData: {
            deliveryPersonId: selectedRider.userId,
            deliveryPersonName: `${selectedRider.f_name} ${selectedRider.l_name || ''}`.trim(),
            deliveryPersonMobile: selectedRider.mobileNo
          }
        })
      });
      
      if (!routeUpdateResponse.ok) {
        throw new Error('Failed to update route');
      }
      
      // Step 2: Update all orders in the route with rider
      const ordersUpdateResponse = await fetch(`${appConfig.api.baseURL}/orders/bulk-update-orders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem(appConfig.storage.token)}`
        },
        body: JSON.stringify({
          orderIds: route.orderIds || [],
          updateData: {
            deliveryPersonMobile: selectedRider.userId,
            deliveryPersonName: `${selectedRider.f_name} ${selectedRider.l_name || ''}`.trim(),
            deliveryFrom: 'PILOT',
            routeId: route.routeId,
            order_status: 'assigned'
          }
        })
      });
      
      if (!ordersUpdateResponse.ok) {
        throw new Error('Failed to update orders');
      }
      
      actions.showSnackbar(`Route ${route.routeId} assigned to ${selectedRider.f_name} successfully`, 'success');
      handleCloseAssignDialog();
      fetchRoutes(); // Refresh routes list
    } catch (error) {
      console.error('Error assigning rider:', error);
      actions.showSnackbar(error.message || 'Failed to assign rider', 'error');
    } finally {
      setAssignDialog(prev => ({ ...prev, loading: false }));
    }
  };

  // Handle modal close
  const handleCloseModal = () => {
    setRouteDetailsModalOpen(false);
    setSelectedRouteId(null);
  };
  
  // Handle route deleted from modal
  const handleRouteDeleted = (deletedRouteId) => {
    actions.showSnackbar(`Route ${deletedRouteId} deleted successfully`, 'success');
    fetchRoutes(); // Refresh the routes list
  };

  // Handle pagination
  const handleChangePage = (event, newPage) => {
    setPagination(prev => ({
      ...prev,
      page: newPage + 1 // MUI uses 0-based indexing, our API uses 1-based
    }));
  };

  const handleChangeRowsPerPage = (event) => {
    const newLimit = parseInt(event.target.value, 10);
    setPagination(prev => ({
      ...prev,
      limit: newLimit,
      page: 1 // Reset to first page when changing page size
    }));
  };

  // Load routes on component mount and filter changes
  useEffect(() => {
    fetchRoutes();
  }, [pagination.page, pagination.limit, statusFilter, riderFilter, searchTerm]);

  // Load riders on component mount
  useEffect(() => {
    fetchRiders();
  }, []);

  // Handle refresh
  const handleRefresh = () => {
    fetchRoutes();
    fetchRiders();
  };

  // Handle search (debounced)
  useEffect(() => {
    const timer = setTimeout(() => {
      // Reset to first page when searching
      setPagination(prev => ({ ...prev, page: 1 }));
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1" sx={{ mb: 1, fontWeight: 'bold' }}>
          Delivery Routes
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage and track all delivery routes
        </Typography>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <RouteIcon color="primary" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h6" component="div">
                    {routes.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Routes
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <DirectionsIcon color="warning" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h6" component="div">
                    {routes.filter(r => r.status === 'active').length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Active Routes
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Chip 
                  label={routes.filter(r => r.status === 'assigned').length}
                  color="primary" 
                  sx={{ mr: 2, fontSize: '1.1rem', fontWeight: 'bold' }}
                />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Assigned Routes
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Chip 
                  label={routes.filter(r => r.status === 'completed').length}
                  color="success" 
                  sx={{ mr: 2, fontSize: '1.1rem', fontWeight: 'bold' }}
                />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Completed Routes
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters and Actions */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ 
          display: 'flex', 
          gap: 2, 
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', flex: 1 }}>
            {/* Search */}
            <TextField
              placeholder="Search routes..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
              sx={{ minWidth: 200 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />

            {/* Status Filter */}
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="created">Created</MenuItem>
                <MenuItem value="assigned">Assigned</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>

            {/* Rider Filter */}
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Rider</InputLabel>
              <Select
                value={riderFilter}
                label="Rider"
                onChange={(e) => setRiderFilter(e.target.value)}
              >
                <MenuItem value="all">All Riders</MenuItem>
                {riders.map((rider) => (
                  <MenuItem key={rider.userId} value={rider.userId}>
                    {rider.f_name} {rider.l_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Actions */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Refresh">
              <IconButton onClick={handleRefresh} disabled={loading}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Routes Table */}
      <Paper sx={{ mb: 2 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Route ID</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Orders</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Delivery Person</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Created</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Assigned</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : routes.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                      {searchTerm || statusFilter !== 'all' || riderFilter !== 'all' 
                        ? 'No routes found matching your filters' 
                        : 'No routes available'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                routes.map((route) => (
                  <TableRow key={route.routeId} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {route.routeId}
                      </Typography>
                    </TableCell>
                    
                    <TableCell>
                      <Chip 
                        label={getStatusLabel(route.status)}
                        color={getStatusColor(route.status)}
                        size="small"
                      />
                    </TableCell>
                    
                    <TableCell>
                      <Typography variant="body2">
                        {route.orderIds ? route.orderIds.length : 0} orders
                      </Typography>
                      {route.orderIds && route.orderIds.length > 0 && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {route.orderIds.slice(0, 2).join(', ')}
                          {route.orderIds.length > 2 && ` +${route.orderIds.length - 2} more`}
                        </Typography>
                      )}
                    </TableCell>
                    
                    <TableCell>
                      {route.deliveryPersonName ? (
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {route.deliveryPersonName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {route.deliveryPersonMobile}
                          </Typography>
                        </Box>
                      ) : (
                        <Chip label="Not Assigned" size="small" variant="outlined" />
                      )}
                    </TableCell>
                    
                    <TableCell>
                      <Typography variant="body2">
                        {formatDateTime(route.createdAt)}
                      </Typography>
                    </TableCell>
                    
                    <TableCell>
                      <Typography variant="body2">
                        {formatDateTime(route.assignedAt)}
                      </Typography>
                    </TableCell>
                    
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="View Details">
                          <IconButton 
                            size="small" 
                            onClick={() => handleViewRoute(route)}
                          >
                            <ViewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        
                        {route.status === 'active' && (
                          <Tooltip title="Track Route">
                            <IconButton 
                              size="small" 
                              onClick={() => handleTrackRoute(route)}
                              color="primary"
                            >
                              <DirectionsIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        
                        {/* Show assign button if NO rider is assigned and route is not completed/cancelled */}
                        {!route.deliveryPersonId && route.status !== 'completed' && route.status !== 'cancelled' && (
                          <Tooltip title="Assign Rider">
                            <IconButton 
                              size="small" 
                              onClick={() => handleOpenAssignDialog(route)}
                              color="success"
                            >
                              <AssignRiderIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        
                        {/* Show de-assign button if rider is assigned and route is not completed */}
                        {route.deliveryPersonId && route.status !== 'completed' && route.status !== 'cancelled' && (
                          <Tooltip title="Remove Rider">
                            <IconButton 
                              size="small" 
                              onClick={() => handleOpenDeassignDialog(route)}
                              color="error"
                            >
                              <RemoveRiderIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Pagination */}
      {pagination.total > 0 && (
        <TablePagination
          component="div"
          count={pagination.total}
          page={pagination.page - 1} // Convert to 0-based for MUI
          onPageChange={handleChangePage}
          rowsPerPage={pagination.limit}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
          labelRowsPerPage="Routes per page:"
          labelDisplayedRows={({ from, to, count }) => 
            `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
          }
          sx={{
            borderTop: 1,
            borderColor: 'divider',
            '& .MuiTablePagination-toolbar': {
              paddingLeft: 2,
              paddingRight: 2,
            }
          }}
        />
      )}

      {/* Route Details Modal */}
      <RouteDetailsModal
        open={routeDetailsModalOpen}
        onClose={handleCloseModal}
        routeId={selectedRouteId}
        onRouteDeleted={handleRouteDeleted}
      />
      
      {/* De-assignment Confirmation Dialog */}
      <Dialog
        open={deassignDialog.open}
        onClose={handleCloseDeassignDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: 'warning.main' }}>
          Remove Rider from Route
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to remove the rider from this route?
          </Typography>
          {deassignDialog.route && (
            <Box
              sx={{
                p: 2,
                backgroundColor: 'background.default',
                borderRadius: 1,
                border: 1,
                borderColor: 'divider'
              }}
            >
              <Typography variant="body2" color="text.secondary">
                <strong>Route ID:</strong> {deassignDialog.route.routeId}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Current Rider:</strong> {deassignDialog.route.deliveryPersonName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Orders:</strong> {deassignDialog.route.orderIds?.length || 0} orders
              </Typography>
            </Box>
          )}
          <Alert severity="warning" sx={{ mt: 2 }}>
            This will remove the rider assignment from the route and all orders in it. The route status will be reset to "Created".
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeassignDialog} disabled={deassignDialog.loading}>
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmDeassign} 
            color="error" 
            variant="contained"
            disabled={deassignDialog.loading}
            startIcon={deassignDialog.loading ? <CircularProgress size={16} /> : null}
          >
            {deassignDialog.loading ? 'Removing...' : 'Remove Rider'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Initial Assignment Dialog */}
      <Dialog
        open={assignDialog.open}
        onClose={handleCloseAssignDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Assign Rider to Route
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Select a rider to assign this route to:
          </Typography>
          {assignDialog.route && (
            <Box
              sx={{
                p: 2,
                mb: 2,
                backgroundColor: 'background.default',
                borderRadius: 1,
                border: 1,
                borderColor: 'divider'
              }}
            >
              <Typography variant="body2" color="text.secondary">
                <strong>Route ID:</strong> {assignDialog.route.routeId}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Status:</strong> {getStatusLabel(assignDialog.route.status)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Orders:</strong> {assignDialog.route.orderIds?.length || 0} orders
              </Typography>
            </Box>
          )}
          
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Search by rider name or mobile number, then select from results.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Name or mobile number"
                value={assignDialog.searchTerm}
                onChange={(e) => setAssignDialog(prev => ({ ...prev, searchTerm: e.target.value }))}
                onKeyDown={(e) => e.key === 'Enter' && handleAssignDialogSearchRiders()}
                disabled={assignDialog.searchLoading}
              />
              <Button
                variant="outlined"
                onClick={handleAssignDialogSearchRiders}
                disabled={assignDialog.searchLoading}
                startIcon={assignDialog.searchLoading ? <CircularProgress size={18} /> : <SearchIcon />}
              >
                {assignDialog.searchLoading ? 'Searching...' : 'Search'}
              </Button>
            </Box>
            <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
              {assignDialog.searchLoading && (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>Searching...</Typography>
              )}
              {!assignDialog.searchLoading && assignDialog.searchResults.length === 0 && assignDialog.searchTerm.trim() && (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>No riders found. Try another search.</Typography>
              )}
              {!assignDialog.searchLoading && !assignDialog.searchTerm.trim() && (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>Search for a rider first.</Typography>
              )}
              {!assignDialog.searchLoading && assignDialog.searchResults.map((rider) => {
                const isSelected = assignDialog.selectedRider?.userId === rider.userId;
                return (
                  <Box
                    key={rider.userId}
                    onClick={() => setAssignDialog(prev => ({ ...prev, selectedRider: rider }))}
                    sx={{
                      py: 1.5,
                      px: 2,
                      mb: 0.5,
                      cursor: 'pointer',
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: isSelected ? 'primary.main' : 'divider',
                      backgroundColor: isSelected ? 'action.selected' : 'transparent',
                      boxShadow: 1,
                      transition: 'background-color 0.2s, border-color 0.2s, box-shadow 0.2s',
                      '&:hover': {
                        backgroundColor: isSelected ? 'action.selected' : 'action.hover',
                        boxShadow: 2
                      },
                      '&:active': {
                        transform: 'scale(0.99)'
                      }
                    }}
                  >
                    <Typography variant="body1">
                      {rider.f_name} {rider.l_name || ''} - {rider.mobileNo}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>
          
          <Alert severity="info" sx={{ mt: 2 }}>
            This will assign the route and all its orders to the selected rider. A notification will be sent to the rider.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAssignDialog} disabled={assignDialog.loading}>
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmAssign} 
            color="success" 
            variant="contained"
            disabled={assignDialog.loading || !assignDialog.selectedRider}
            startIcon={assignDialog.loading ? <CircularProgress size={16} /> : null}
          >
            {assignDialog.loading ? 'Assigning...' : 'Assign Rider'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Routes;
